import { useState, useCallback } from 'react'

import { store } from 'app/initStore'
import { useFlowPicker } from 'apps/flowPicker/hooks/useFlowPicker'
import { FlowPickerSource } from 'apps/flowPicker/lib/constants'
import { FlowPickerActions } from 'apps/flowPicker/store'
import { updateNode } from 'common/builder/actions/updateNode'
import { GotoServiceInterface } from 'common/builder/components/chart/FlowChart/lib/services/GotoService/GotoServiceInterface'
import { navigatePollyfill } from 'utils/router/tools'
import { getFlowUrlByPath } from 'utils/router/tools/cms'
import { analyticsService, flowBuilderEvents } from 'utils/services/analytics'
import errorTrackingService from 'utils/services/errorTrackingService'

export const useGotoService = (initialBuilderId: string): GotoServiceInterface => {
  const [builderId] = useState(initialBuilderId)
  const [gotoNodeId, setGotoNodeId] = useState<string | null>(null)

  const openFlow = useCallback((flowId?: string, path?: string | null) => {
    try {
      const pathOrFlowId = path || flowId

      if (pathOrFlowId) {
        analyticsService.logFlowBuilderEvent(flowBuilderEvents.GOTO_ANOTHER_FLOW)
        navigatePollyfill(getFlowUrlByPath(pathOrFlowId))
      }
    } catch (err) {
      errorTrackingService.trackError(err, {
        fingerprint: 'no-path-parameter-in-flow-data',
      })
    }
  }, [])

  const addFlow = useCallback((nodeId: string) => {
    setGotoNodeId(nodeId)
    store.dispatch(FlowPickerActions.openModal({ source: FlowPickerSource.FLOW_CHART }))
  }, [])

  const selectFlow = useCallback(
    (flowId: string) => {
      if (gotoNodeId === null) {
        return
      }
      store.dispatch(updateNode(builderId, gotoNodeId, { flowId }))
      setGotoNodeId(null)
    },
    [builderId, gotoNodeId],
  )

  const clearFlow = useCallback(
    (nodeId: string) => {
      store.dispatch(updateNode(builderId, nodeId, { flowId: null }))
    },
    [builderId],
  )

  useFlowPicker({
    source: FlowPickerSource.FLOW_CHART,
    onFlowSelect: selectFlow,
  })

  return {
    openFlow,
    addFlow,
    clearFlow,
  }
}
